import { App } from './App';
import './assets/icons/custom-icons';
import { getOrCreateDatadogLogsModel } from './components/Datadog/DatadogLogModel';
import { InitializationError } from './components/ErrorPage/Errors';
import { getOrCreateInitializedLogRocketModel } from './components/LogRocket/LogRocketModel';
import {
	APPCUES_ACCOUNT_ID,
	APPCUES_ENABLED,
	DATADOG_APPLICATION_ID,
	DATADOG_CLIENT_TOKEN,
	ENVIRONMENT_NAME,
} from './config/Environment';
import { sendOnClickAnalytics } from './index.analytics';
import { AuthStoreContext, getOrCreateAuthStore } from './stores/authStore';
import { UserConfigStore, UserConfigStoreContext } from './stores/userConfigStore';
import antdTheme from './theme/antd-tokens/light.json' with { type: 'json' };
import { ANTD_COMPONENTS_CONFIG } from './theme/theme';
import { withAnalyticNamespaceOnClickEventDelegation } from './utilities/analytics/eventDelegation';
import '@ant-design/v5-patch-for-react-19';
import styled from '@emotion/styled';
import { ConfigProvider } from 'antd';
import { lazy, StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { config as zodConfig } from 'zod/mini';

zodConfig({
	jitless: true,
});

const AppcuesStarterLazy = lazy(() =>
	import('./third-parties/AppcuesStarter').then((module) => ({
		default: module.AppcuesStarter,
	})),
);

getOrCreateInitializedLogRocketModel();

if (DATADOG_CLIENT_TOKEN && DATADOG_APPLICATION_ID) {
	const { datadogRum } = await import('@datadog/browser-rum');

	datadogRum.init({
		applicationId: DATADOG_APPLICATION_ID,
		clientToken: DATADOG_CLIENT_TOKEN,
		site: 'datadoghq.com',
		service: 'navina-app',
		env: ENVIRONMENT_NAME,
		// Specify a version number to identify the deployed version of your application in Datadog
		// version: '1.0.0',
		sessionSampleRate: 100,
		sessionReplaySampleRate: 0,
		trackUserInteractions: true,
		trackResources: true,
		trackLongTasks: true,
		defaultPrivacyLevel: 'mask-user-input',
	});

	getOrCreateDatadogLogsModel('navina-frontend-app');
}

const NavinaAppContainer = styled.div`
	block-size: 100%;
	inline-size: 100%;
`;

const AuthStoreProvider = AuthStoreContext.Provider;
const authStore = getOrCreateAuthStore();

const UserConfigStoreProvider = UserConfigStoreContext.Provider;
const userConfigStore = new UserConfigStore(authStore);

const container = document.getElementById('root');
if (container === null) {
	throw new InitializationError('Root container not found');
}

const root = createRoot(container);

root.render(
	<StrictMode>
		<NavinaAppContainer
			{...{
				...withAnalyticNamespaceOnClickEventDelegation(sendOnClickAnalytics),
			}}
		>
			<AuthStoreProvider value={authStore}>
				<UserConfigStoreProvider value={userConfigStore}>
					<ConfigProvider theme={{ token: antdTheme, components: ANTD_COMPONENTS_CONFIG }}>
						<App />
					</ConfigProvider>
				</UserConfigStoreProvider>
			</AuthStoreProvider>
		</NavinaAppContainer>
		{APPCUES_ENABLED && (
			<Suspense {...{ fallback: null }}>
				<AppcuesStarterLazy {...{ appcuesAccountId: APPCUES_ACCOUNT_ID }} />
			</Suspense>
		)}
	</StrictMode>,
);
